package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminAccessAttendanceMode
import org.gymly.model.AdminAccessEvent
import org.gymly.model.AdminAccessTerminal
import org.gymly.model.AdminAccessTerminalUpsert
import org.gymly.model.AdminClient
import org.gymly.model.AdminSettingsModules
import org.gymly.model.AdminSettingsModulesUpsert
import org.gymly.model.AdminWorker
import org.gymly.theme.AdminColors
import org.gymly.ui.components.PasswordVisibilityToggle
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.ui.components.rememberPasswordVisible
import org.gymly.util.userMessage

private data class SimulatePick(
    val clientId: String? = null,
    val staffId: String? = null,
    val label: String,
)

@Composable
internal fun SettingsAccessPanel(
    compact: Boolean,
    modules: AdminSettingsModules = AdminSettingsModules(accessEnabled = true, exchangeRatesEnabled = true),
    onModulesChange: (AdminSettingsModules) -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var savingModule by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }
    var actionTerminalId by remember { mutableStateOf<String?>(null) }
    var actionBusy by remember { mutableStateOf(false) }
    var actionMsg by remember { mutableStateOf<String?>(null) }
    var actionIsError by remember { mutableStateOf(false) }
    var terminals by remember { mutableStateOf<List<AdminAccessTerminal>>(emptyList()) }
    var events by remember { mutableStateOf<List<AdminAccessEvent>>(emptyList()) }
    var companyId by remember { mutableStateOf<String?>(null) }
    var reviewTerminal by remember { mutableStateOf<AdminAccessTerminal?>(null) }
    reviewTerminal?.let { t ->
        TerminalReconcileDialog(terminal = t, onDismiss = { reviewTerminal = null })
    }
    var clients by remember { mutableStateOf<List<AdminClient>>(emptyList()) }
    var workers by remember { mutableStateOf<List<AdminWorker>>(emptyList()) }
    var editing by remember { mutableStateOf<AdminAccessTerminal?>(null) }
    var creating by remember { mutableStateOf(false) }
    var graceDaysInput by remember(modules.membershipAccessGraceDays) {
        mutableStateOf(modules.membershipAccessGraceDays.toString())
    }

    fun reload(silent: Boolean = false) {
        if (!modules.accessEnabled) {
            loading = false
            return
        }
        scope.launch {
            if (!silent) {
                loading = true
                error = null
            }
            try {
                terminals = AdminRepo.api.listAccessTerminals()
                events = runCatching { AdminRepo.api.listAccessEvents(8) }.getOrDefault(emptyList())
                companyId = runCatching { AdminRepo.api.getCompany().id }.getOrElse {
                    AdminRepo.api.listBranches().firstOrNull()?.id
                }
                clients = runCatching { AdminRepo.api.listClients() }.getOrDefault(emptyList())
                workers = runCatching { AdminRepo.api.listWorkers() }.getOrDefault(emptyList())
                    .filter { it.active }
            } catch (e: Throwable) {
                if (!silent) {
                    error = e.userMessage("No se pudo cargar el acceso.")
                }
            } finally {
                if (!silent) loading = false
            }
        }
    }

    LaunchedEffect(modules.accessEnabled) {
        if (!modules.accessEnabled) {
            loading = false
            return@LaunchedEffect
        }
        reload()
    }

    VerticalScrollColumn(
        Modifier
            .fillMaxSize()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(if (compact) 16.dp else 24.dp),
    ) {
        Text("Terminal de acceso (Hikvision)", fontWeight = FontWeight.Bold, fontSize = 16.sp)
        Spacer(Modifier.height(6.dp))
        Text(
            "Activa el DS-K1T342 (rostro / huella). En el equipo, notificación HTTP hacia el webhook de Gymly. " +
                "Al reconocer a un socio o trabajador se marca asistencia (o reserva del día).",
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
        )
        Spacer(Modifier.height(12.dp))
        error?.let { Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp)) }
        success?.let { Text(it, color = AdminColors.Success, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp)) }

        SettingsModuleEnableCard(
            title = "Módulo de acceso",
            description = "Activa o desactiva la integración Hikvision, terminales, webhooks y el widget de acceso en vivo del panel.",
            enabledLabel = "Control de acceso habilitado en este gym",
            checked = modules.accessEnabled,
            saving = savingModule,
            onCheckedChange = { checked ->
                scope.launch {
                    savingModule = true
                    error = null
                    try {
                        onModulesChange(
                            AdminRepo.api.updateSettingsModules(
                                AdminSettingsModulesUpsert(
                                    accessEnabled = checked,
                                    exchangeRatesEnabled = modules.exchangeRatesEnabled,
                                    suppressAccessWhileCapturing = modules.suppressAccessWhileCapturing,
                                    clientAccountsEnabled = modules.clientAccountsEnabled,
                                    schedulesAutomatic = modules.schedulesAutomatic,
                                ),
                            ),
                        )
                        success = if (checked) "Módulo de acceso activado." else "Módulo de acceso desactivado."
                    } catch (e: Throwable) {
                        error = e.userMessage("No se pudo actualizar el módulo de acceso.")
                    } finally {
                        savingModule = false
                    }
                }
            },
            modifier = Modifier.padding(bottom = 12.dp),
        )

        if (modules.accessEnabled) {
            Row(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(12.dp))
                    .background(AdminColors.Surface)
                    .padding(horizontal = 12.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Checkbox(
                    checked = modules.suppressAccessWhileCapturing,
                    onCheckedChange = { checked ->
                        scope.launch {
                            savingModule = true
                            error = null
                            try {
                                onModulesChange(
                                    AdminRepo.api.updateSettingsModules(
                                        AdminSettingsModulesUpsert(
                                            accessEnabled = modules.accessEnabled,
                                            exchangeRatesEnabled = modules.exchangeRatesEnabled,
                                            suppressAccessWhileCapturing = checked,
                                            clientAccountsEnabled = modules.clientAccountsEnabled,
                                            schedulesAutomatic = modules.schedulesAutomatic,
                                        ),
                                    ),
                                )
                                success = if (checked) {
                                    "Durante la captura no se registra entrada ni se abre la puerta."
                                } else {
                                    "La captura ya no suprime entradas (el terminal puede abrir al reconocer)."
                                }
                            } catch (e: Throwable) {
                                error = e.userMessage("No se pudo guardar la opción de captura.")
                            } finally {
                                savingModule = false
                            }
                        }
                    },
                    enabled = !savingModule,
                    colors = CheckboxDefaults.colors(
                        checkedColor = AdminColors.Primary,
                        uncheckedColor = AdminColors.TextMuted,
                    ),
                )
                Column(Modifier.padding(start = 4.dp)) {
                    Text(
                        "Al capturar rostro: no registrar asistencia ni abrir puerta",
                        color = AdminColors.Text,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Medium,
                    )
                    Text(
                        "Recomendado al enrolar. Si el Hikvision abre por reconocimiento, Gymly intenta cerrar y no crea sesión.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }
            }
            Spacer(Modifier.height(12.dp))
            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(12.dp))
                    .background(AdminColors.Surface)
                    .padding(12.dp),
            ) {
                Text(
                    "Prórroga tras vencimiento",
                    color = AdminColors.Text,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.Medium,
                )
                Text(
                    "Días extra de acceso después del fin del plan (Gymly + fecha Valid en Hikvision). " +
                        "0 = sin prórroga. No cambia la fecha de facturación del plan.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp, bottom = 8.dp),
                )
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    OutlinedTextField(
                        value = graceDaysInput,
                        onValueChange = { raw ->
                            graceDaysInput = raw.filter { it.isDigit() }.take(2)
                        },
                        modifier = Modifier.width(88.dp),
                        singleLine = true,
                        label = { Text("Días") },
                        enabled = !savingModule,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = AdminColors.Primary,
                            unfocusedBorderColor = AdminColors.Outline,
                        ),
                    )
                    Button(
                        onClick = {
                            val days = graceDaysInput.toIntOrNull()?.coerceIn(0, 30) ?: 0
                            graceDaysInput = days.toString()
                            scope.launch {
                                savingModule = true
                                error = null
                                try {
                                    onModulesChange(
                                        AdminRepo.api.updateSettingsModules(
                                            AdminSettingsModulesUpsert(
                                                accessEnabled = modules.accessEnabled,
                                                exchangeRatesEnabled = modules.exchangeRatesEnabled,
                                                suppressAccessWhileCapturing = modules.suppressAccessWhileCapturing,
                                                clientAccountsEnabled = modules.clientAccountsEnabled,
                                                schedulesAutomatic = modules.schedulesAutomatic,
                                                membershipAccessGraceDays = days,
                                            ),
                                        ),
                                    )
                                    success = if (days == 0) {
                                        "Sin prórroga: el acceso corta al vencer el plan."
                                    } else {
                                        "Prórroga de $days día(s). Re-sincroniza socios en el terminal si ya estaban enrolados."
                                    }
                                } catch (e: Throwable) {
                                    error = e.userMessage("No se pudo guardar la prórroga.")
                                } finally {
                                    savingModule = false
                                }
                            }
                        },
                        enabled = !savingModule,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text(if (savingModule) "…" else "Guardar")
                    }
                }
            }
            Spacer(Modifier.height(16.dp))
        }

        if (!modules.accessEnabled) {
            return@VerticalScrollColumn
        }

        when {
            loading -> Box(Modifier.fillMaxWidth().padding(40.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            creating || editing != null -> TerminalForm(
                initial = editing,
                companyId = companyId,
                saving = saving,
                onCancel = {
                    creating = false
                    editing = null
                },
                onSave = { upsert ->
                    scope.launch {
                        saving = true
                        error = null
                        success = null
                        try {
                            val saved = if (editing == null) {
                                AdminRepo.api.createAccessTerminal(upsert)
                            } else {
                                AdminRepo.api.updateAccessTerminal(editing!!.id, upsert)
                            }
                            success = "Terminal «${saved.name}» guardado."
                            creating = false
                            editing = null
                            reload()
                        } catch (e: Throwable) {
                            error = e.userMessage("No se pudo guardar el terminal.")
                        } finally {
                            saving = false
                        }
                    }
                },
            )
            else -> {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    Button(
                        onClick = { creating = true },
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) { Text("+ Añadir terminal") }
                }
                Spacer(Modifier.height(12.dp))
                if (terminals.isEmpty()) {
                    Text("Ningún terminal configurado todavía.", color = AdminColors.TextMuted, fontSize = 13.sp)
                } else {
                    terminals.forEach { t ->
                        TerminalCard(
                            terminal = t,
                            clients = clients,
                            workers = workers,
                            busy = actionBusy && actionTerminalId == t.id,
                            feedback = actionMsg?.takeIf { actionTerminalId == t.id },
                            feedbackIsError = actionIsError,
                            onEdit = { editing = t },
                            onReview = { reviewTerminal = t },
                            onTest = {
                                scope.launch {
                                    actionTerminalId = t.id
                                    actionBusy = true
                                    actionMsg = "Probando conexión…"
                                    actionIsError = false
                                    error = null
                                    success = null
                                    try {
                                        val r = AdminRepo.api.testAccessTerminal(t.id)
                                        actionMsg = r.message
                                        actionIsError = false
                                        success = r.message
                                        reload(silent = true)
                                    } catch (e: Throwable) {
                                        val msg = e.userMessage("No hay conexión con el terminal.")
                                        actionMsg = msg
                                        actionIsError = true
                                        error = msg
                                    } finally {
                                        actionBusy = false
                                    }
                                }
                            },
                            onConfigureWebhook = {
                                scope.launch {
                                    actionTerminalId = t.id
                                    actionBusy = true
                                    actionMsg = "Configurando webhook en el Hikvision…"
                                    actionIsError = false
                                    error = null
                                    success = null
                                    try {
                                        val r = AdminRepo.api.configureAccessTerminalWebhook(t.id)
                                        actionMsg = r.message
                                        actionIsError = false
                                        success = r.message
                                        reload(silent = true)
                                    } catch (e: Throwable) {
                                        val msg = e.userMessage("No se pudo configurar el webhook en el Hikvision.")
                                        actionMsg = msg
                                        actionIsError = true
                                        error = msg
                                    } finally {
                                        actionBusy = false
                                    }
                                }
                            },
                            onSimulate = { pick ->
                                scope.launch {
                                    actionTerminalId = t.id
                                    actionBusy = true
                                    actionMsg = null
                                    actionIsError = false
                                    error = null
                                    success = null
                                    try {
                                        val ev = AdminRepo.api.simulateAccessCheckIn(
                                            terminalId = t.id,
                                            clientId = pick.clientId,
                                            staffId = pick.staffId,
                                        )
                                        val msg = "${ev.clientName ?: pick.label}: ${ev.resultLabel}"
                                        actionMsg = msg
                                        success = msg
                                        reload(silent = true)
                                    } catch (e: Throwable) {
                                        val msg = e.userMessage("No se pudo simular el check-in.")
                                        actionMsg = msg
                                        actionIsError = true
                                        error = msg
                                    } finally {
                                        actionBusy = false
                                    }
                                }
                            },
                            onDelete = {
                                scope.launch {
                                    try {
                                        AdminRepo.api.deleteAccessTerminal(t.id)
                                        reload()
                                    } catch (e: Throwable) {
                                        error = e.userMessage("No se pudo eliminar.")
                                    }
                                }
                            },
                        )
                        Spacer(Modifier.height(12.dp))
                    }
                }
                if (events.isNotEmpty()) {
                    Spacer(Modifier.height(8.dp))
                    Text("Últimos eventos", fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
                    Text(
                        "Reconocimientos, denegaciones y personal. " +
                            "En Sesiones de piso: 1 entrada por socio/trabajador y día.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(top = 2.dp, bottom = 6.dp),
                    )
                    events.forEach { ev ->
                        Text(
                            "${ev.clientName ?: "—"} · ${ev.resultLabel} · ${ev.terminalName}",
                            color = AdminColors.Text,
                            fontSize = 13.sp,
                            modifier = Modifier.padding(vertical = 3.dp),
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun TerminalCard(
    terminal: AdminAccessTerminal,
    clients: List<AdminClient>,
    workers: List<AdminWorker>,
    busy: Boolean = false,
    feedback: String? = null,
    feedbackIsError: Boolean = false,
    onEdit: () -> Unit,
    onTest: () -> Unit,
    onConfigureWebhook: () -> Unit,
    onSimulate: (SimulatePick) -> Unit,
    onReview: () -> Unit,
    onDelete: () -> Unit,
) {
    var simMenu by remember { mutableStateOf(false) }
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.Surface)
            .padding(14.dp),
    ) {
        Text(terminal.name, fontWeight = FontWeight.Bold, fontSize = 15.sp)
        Text(
            "${if (terminal.enabled) "Activo" else "Inactivo"} · ${terminal.host}:${terminal.port} · ${terminal.attendanceMode.labelEs()}",
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        terminal.lastError?.let {
            Text(it, color = AdminColors.Error, fontSize = 12.sp, modifier = Modifier.padding(top = 4.dp))
        }
        if (!terminal.webhookSecret.isNullOrBlank()) {
            Text(
                "Host Gymly: ${terminal.webhookNotifyHost ?: "—"}:${terminal.webhookNotifyPort} · " +
                    "Webhook: /api/webhooks/hikvision/${terminal.id}?secret=…",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 6.dp),
            )
            Text(
                "Usa «Configurar webhook» para enviar la URL al Hikvision. Puerto 443 usa HTTPS (si queda en HTTP, abre puerta pero no registra).",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
        }
        Spacer(Modifier.height(10.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedButton(onClick = onEdit, enabled = !busy) { Text("Editar", color = AdminColors.Text) }
            OutlinedButton(onClick = onTest, enabled = !busy) { Text("Probar", color = AdminColors.Text) }
            OutlinedButton(onClick = onConfigureWebhook, enabled = !busy) {
                Text(if (busy) "…" else "Configurar webhook", color = AdminColors.Text)
            }
            OutlinedButton(onClick = onReview, enabled = !busy) {
                Text(
                    if (terminal.pendingReview > 0) "Revisar usuarios (${terminal.pendingReview})" else "Revisar usuarios",
                    color = AdminColors.Text,
                )
            }
            Box {
                OutlinedButton(onClick = { simMenu = true }, enabled = !busy) {
                    Text("Simular entrada", color = AdminColors.Text)
                }
                DropdownMenu(expanded = simMenu, onDismissRequest = { simMenu = false }) {
                    if (clients.isEmpty() && workers.isEmpty()) {
                        DropdownMenuItem(
                            text = { Text("No hay clientes ni trabajadores") },
                            onClick = { simMenu = false },
                        )
                    } else {
                        if (clients.isNotEmpty()) {
                            DropdownMenuItem(
                                text = { Text("— Clientes —", fontWeight = FontWeight.SemiBold) },
                                onClick = {},
                                enabled = false,
                            )
                            clients.take(15).forEach { c ->
                                DropdownMenuItem(
                                    text = { Text(c.fullName) },
                                    onClick = {
                                        simMenu = false
                                        onSimulate(SimulatePick(clientId = c.id, label = c.fullName))
                                    },
                                )
                            }
                        }
                        if (workers.isNotEmpty()) {
                            DropdownMenuItem(
                                text = { Text("— Personal —", fontWeight = FontWeight.SemiBold) },
                                onClick = {},
                                enabled = false,
                            )
                            workers.take(15).forEach { w ->
                                DropdownMenuItem(
                                    text = { Text(w.displayName) },
                                    onClick = {
                                        simMenu = false
                                        onSimulate(SimulatePick(staffId = w.id, label = w.displayName))
                                    },
                                )
                            }
                        }
                    }
                }
            }
            TextButton(onClick = onDelete, enabled = !busy) { Text("Eliminar", color = AdminColors.Error) }
        }
        feedback?.let { msg ->
            Spacer(Modifier.height(8.dp))
            Text(
                msg,
                color = if (feedbackIsError) AdminColors.Error else AdminColors.Success,
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium,
            )
        }
    }
}

@Composable
private fun TerminalForm(
    initial: AdminAccessTerminal?,
    companyId: String?,
    saving: Boolean,
    onCancel: () -> Unit,
    onSave: (AdminAccessTerminalUpsert) -> Unit,
) {
    var name by remember { mutableStateOf(initial?.name.orEmpty().ifBlank { "Recepción" }) }
    var enabled by remember { mutableStateOf(initial?.enabled ?: true) }
    var host by remember { mutableStateOf(initial?.host.orEmpty()) }
    var port by remember { mutableStateOf((initial?.port ?: 80).toString()) }
    var username by remember { mutableStateOf(initial?.username.orEmpty().ifBlank { "admin" }) }
    var password by remember { mutableStateOf("") }
    val branchId = companyId
    var mode by remember { mutableStateOf(initial?.attendanceMode ?: AdminAccessAttendanceMode.BOTH) }
    var notifyHost by remember { mutableStateOf(initial?.webhookNotifyHost.orEmpty()) }
    var notifyPort by remember { mutableStateOf((initial?.webhookNotifyPort ?: 18080).toString()) }
    var modeMenu by remember { mutableStateOf(false) }
    var formError by remember { mutableStateOf<String?>(null) }

    Column {
        Text(if (initial == null) "Nuevo terminal" else "Editar terminal", fontWeight = FontWeight.SemiBold)
        Spacer(Modifier.height(10.dp))
        AccessField("Nombre", name, { name = it }, "Recepción · entrada")
        Row(verticalAlignment = Alignment.CenterVertically) {
            Checkbox(
                checked = enabled,
                onCheckedChange = { enabled = it },
                colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
            )
            Text("Activado", fontSize = 14.sp)
        }
        AccessField("IP o host", host, { host = it }, "ej. 192.168.1.50")
        AccessField("Puerto", port, { port = it }, "80")
        AccessField("Usuario ISAPI", username, { username = it }, "admin")
        AccessField(
            if (initial == null) "Contraseña ISAPI" else "Contraseña (vacío = no cambiar)",
            password,
            { password = it },
            "escribe la contraseña",
            password = true,
        )
        Spacer(Modifier.height(8.dp))
        Text("Qué registrar", color = AdminColors.TextMuted, fontSize = 12.sp)
        Box {
            OutlinedButton(onClick = { modeMenu = true }, modifier = Modifier.fillMaxWidth()) {
                Text(mode.labelEs(), color = AdminColors.Text)
            }
            DropdownMenu(expanded = modeMenu, onDismissRequest = { modeMenu = false }) {
                AdminAccessAttendanceMode.entries.forEach { m ->
                    DropdownMenuItem(text = { Text(m.labelEs()) }, onClick = { mode = m; modeMenu = false })
                }
            }
        }
        Spacer(Modifier.height(8.dp))
        Text("Webhook → Gymly", color = AdminColors.TextMuted, fontSize = 12.sp)
        AccessField(
            "Host Gymly (IP o dominio visible desde el terminal)",
            notifyHost,
            { notifyHost = it },
            "ej. gymly.tudominio.com",
        )
        AccessField("Puerto Gymly", notifyPort, { notifyPort = it }, "443")
        formError?.let { Text(it, color = AdminColors.Error, fontSize = 12.sp, modifier = Modifier.padding(top = 8.dp)) }
        Spacer(Modifier.height(14.dp))
        Row(horizontalArrangement = Arrangement.End, modifier = Modifier.fillMaxWidth()) {
            TextButton(onClick = onCancel, enabled = !saving) { Text("Cancelar", color = AdminColors.TextMuted) }
            Button(
                onClick = {
                    val nameTrim = name.trim()
                    val hostTrim = host.trim()
                    val userTrim = username.trim()
                    when {
                        nameTrim.isBlank() -> {
                            formError = "Escribe el nombre del terminal (el texto gris es solo ejemplo)."
                            return@Button
                        }
                        hostTrim.isBlank() -> {
                            formError = "Escribe la IP del Hikvision en «IP o host» (ej. 192.168.1.50). El texto gris es solo ejemplo, no está guardado."
                            return@Button
                        }
                        userTrim.isBlank() -> {
                            formError = "Escribe el usuario ISAPI (ej. admin)."
                            return@Button
                        }
                        initial == null && password.isBlank() -> {
                            formError = "Escribe la contraseña ISAPI. Los puntos grises son solo ejemplo."
                            return@Button
                        }
                    }
                    formError = null
                    onSave(
                        AdminAccessTerminalUpsert(
                            name = nameTrim,
                            enabled = enabled,
                            host = hostTrim,
                            port = port.toIntOrNull() ?: 80,
                            username = userTrim,
                            password = password.ifBlank { null },
                            branchId = branchId,
                            attendanceMode = mode,
                            webhookNotifyHost = notifyHost.trim().ifBlank { null },
                            webhookNotifyPort = notifyPort.toIntOrNull() ?: 18080,
                        ),
                    )
                },
                enabled = !saving,
                colors = ButtonDefaults.buttonColors(
                    containerColor = AdminColors.Primary,
                    contentColor = AdminColors.OnBrand,
                ),
            ) { Text(if (saving) "…" else "Guardar") }
        }
    }
}

@Composable
private fun AccessField(
    label: String,
    value: String,
    onValue: (String) -> Unit,
    placeholder: String,
    password: Boolean = false,
) {
    Text(label, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 8.dp, bottom = 4.dp))
    val (visible, toggleVisible) = rememberPasswordVisible()
    OutlinedTextField(
        value = value,
        onValueChange = onValue,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        visualTransformation = if (password && !visible) PasswordVisualTransformation() else androidx.compose.ui.text.input.VisualTransformation.None,
        trailingIcon = if (password) {
            { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible) }
        } else {
            null
        },
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}
